import type { Customer, CustomerRevision } from '@/lib/schemas/db'
import { buildCustomerRevisionChanges } from '@/lib/customerRevisionFields'

import type { NewCustomerInput, UpdateCustomerInput } from '../domain/models'
import type {
  AuthStorePort,
  ClockPort,
  CustomerRevisionsRepositoryPort,
  CustomersRepositoryPort,
  IdGeneratorPort,
} from '../ports'

export function createCustomer(
  input: NewCustomerInput,
  deps: {
    clock: ClockPort
    idGenerator: IdGeneratorPort
    customersRepo: CustomersRepositoryPort
  },
): {
  customer: Customer
  transaction: ReturnType<CustomersRepositoryPort['insert']>
} {
  const timestamp = deps.clock.now()
  const customer: Customer = {
    id: deps.idGenerator.generateId(),
    createdAt: timestamp,
    updatedAt: timestamp,
    ...input,
  }
  const transaction = deps.customersRepo.insert(customer)
  return { customer, transaction }
}

/**
 * Update a customer's details, keeping what they said before.
 *
 * The customer record stays live and holds the current values — the mirror
 * image of a locked service, where the record freezes and addendums stack on
 * top. Here the *history* stacks underneath instead: any edit that changes a
 * tracked field also appends an immutable revision carrying the superseded
 * values, so no detail is ever lost to an overwrite.
 *
 * Against PocketBase the revision inserted here is optimistic display only —
 * the customer-revisions server hook appends the durable record inside the
 * update request itself, so raw API writes are trailed too. In the in-memory
 * adapter this insert IS the store.
 */
export function updateCustomer(
  id: string,
  patch: UpdateCustomerInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    customersRepo: CustomersRepositoryPort
    customerRevisionsRepo: CustomerRevisionsRepositoryPort
  },
) {
  const timestamp = deps.clock.now()
  const before = deps.customersRepo.get(id)
  if (!before) return null

  const changes = buildCustomerRevisionChanges(
    before as unknown as Record<string, string | undefined>,
    patch as Record<string, string | undefined>,
  )
  const currentUser = deps.authStore.user
  if (changes.length > 0 && !currentUser) {
    // No tracked change may land without an author on its revision.
    throw new Error('Not authenticated')
  }

  const transaction = deps.customersRepo.update(id, (draft) => {
    Object.assign(draft, patch)
    draft.updatedAt = timestamp
  })

  if (changes.length > 0 && currentUser) {
    const revision: CustomerRevision = {
      id: deps.idGenerator.generateId(),
      customerId: id,
      authorId: currentUser.id,
      createdAt: timestamp,
      changes,
    }
    deps.customerRevisionsRepo.insert(revision)
  }

  return transaction
}

export function deleteCustomer(
  id: string,
  deps: { customersRepo: CustomersRepositoryPort },
) {
  if (!deps.customersRepo.has(id)) return
  return deps.customersRepo.delete(id)
}
